import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useSelectedComponent } from '@/hooks/useSelectedComponent';
import ComponentNotFound from '@/hooks/ComponentNotFound';
import OnboardingPageHeader from '@/components/smartai/ComponentOnboarding/OnboardingPageHeader';
import OnboardingProjectHeader from '@/components/smartai/ComponentOnboarding/OnboardingProjectHeader';
import { onboardingPageClass } from '@/components/smartai/ComponentOnboarding/onboarding-layout';
import { buildDiffCardClass } from '@/components/smartai/ComponentBuildDiff/build-diff-layout';
import { registerTestIntelligenceScanSession } from '@/components/smartai/api/registerScanSession';
import CallGraphVisualization from './CallGraphVisualization';
import CallGraphSkeleton, {
  CallGraphVisualizationSkeleton,
} from './CallGraphSkeleton';

const formatBranch = (branch: string | undefined): string =>
  (branch || 'main').replace(/^refs\/heads\//, '');

const CallGraphPage = () => {
  const { selected: component, ready, isLoading, notFound } =
    useSelectedComponent();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const scanId = component.scanId?.trim() ?? '';
  const [sessionReady, setSessionReady] = useState(!scanId);

  useEffect(() => {
    if (!scanId) {
      setSessionReady(true);
      return;
    }

    let cancelled = false;
    setSessionReady(false);
    void registerTestIntelligenceScanSession(scanId)
      .then(() => {
        if (!cancelled) {
          void queryClient.invalidateQueries({
            queryKey: ['call-graph', scanId],
          });
        }
      })
      .finally(() => {
        if (!cancelled) setSessionReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [scanId, queryClient]);

  if (isLoading || (!ready && !notFound)) {
    return <CallGraphSkeleton />;
  }
  if (notFound) return <ComponentNotFound />;

  return (
    <div className={onboardingPageClass}>
      <OnboardingPageHeader
        eyebrow="Code Analysis"
        title="Call Graph"
        subtitle="Explore files, methods, and call relationships for the selected component scan."
      />

      <OnboardingProjectHeader
        org={component.orgName}
        project={component.projectName || '—'}
        repoName={component.repoName || '—'}
        branchName={formatBranch(component.branchName) || '—'}
      />

      <section
        className={cn(
          buildDiffCardClass,
          'flex h-[min(72vh,760px)] max-h-[min(72vh,760px)] min-h-0 flex-col overflow-hidden p-0 sm:p-0',
        )}
        aria-label="Call graph">
        {!scanId ? (
          <div className="flex flex-col items-center justify-center gap-4 p-12 text-center text-sm leading-6 text-[var(--inv-text-secondary)]">
            <p className="m-0">
              No scan is selected for this component. Complete onboarding and
              run a code scan, then return here to view the call graph.
            </p>
            <Button
              type="button"
              variant="secondary"
              onClick={() =>
                navigate(
                  `/sia/components/code_analysis/inventory/${component.dbRepoId}`,
                )
              }>
              Go to Inventory
            </Button>
          </div>
        ) : !sessionReady ? (
          <CallGraphVisualizationSkeleton embedded />
        ) : (
          <CallGraphVisualization
            embedded
            scanId={scanId}
            repoUrl={component.repoUrl}
            branchName={component.branchName}
            patToken={component.pat}
          />
        )}
      </section>
    </div>
  );
};

export default CallGraphPage;
